import React, { useCallback, useMemo } from 'react';
import { useGPSLostAddGridToMap } from '../GpsLost/useGPSLostAddGridToMap';
import { useGPSLost } from '../GpsLost/GPSLostContext';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import { PeriodNavigationActions } from './PeriodNavigationActions';

interface GPSLostActionsProps {
  setDisableDragNnDropTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  isDragging: boolean;
  variant?: 'external' | 'inline';
}

export const GPSLostActions = ({
  setDisableDragNnDropTooltip,
  isDragging,
  variant = 'external',
}: GPSLostActionsProps) => {
  const {
    selectedGPSLostConfiguration,
    selectedInstanceIndex,
    setSelectedInstanceIndex,
    instances,
  } = useGPSLost();
  const { addGridToMap, isAdding } = useGPSLostAddGridToMap();

  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const onNext = useCallback(async () => {
    if (selectedInstanceIndex >= instances.length - 1) return;

    const instance = instances[selectedInstanceIndex + 1];
    await addGridToMap(selectedGPSLostConfiguration, instance, false);
    setSelectedInstanceIndex(selectedInstanceIndex + 1);
  }, [
    selectedInstanceIndex,
    instances,
    addGridToMap,
    selectedGPSLostConfiguration,
    setSelectedInstanceIndex,
  ]);

  const onPrevious = useCallback(async () => {
    if (selectedInstanceIndex <= 0) return;

    const instance = instances[selectedInstanceIndex - 1];
    await addGridToMap(selectedGPSLostConfiguration, instance, false);
    setSelectedInstanceIndex(selectedInstanceIndex - 1);
  }, [
    selectedInstanceIndex,
    instances,
    addGridToMap,
    selectedGPSLostConfiguration,
    setSelectedInstanceIndex,
  ]);

  const isPreviousDisabled = selectedInstanceIndex <= 0;
  const isNextDisabled = selectedInstanceIndex >= instances.length - 1;

  const tooltips = useMemo(() => {
    const getTimerangeDisplay = (
      timerange: { lower: Date; upper: Date } | undefined,
    ): string => {
      if (!timerange || !timerange.lower || !timerange.upper)
        return 'Unknown timerange';

      const lowerDate = formatDateTimeWithTimeZone(timerange.lower);
      const upperDate = formatDateTimeWithTimeZone(timerange.upper);

      return `${lowerDate} - ${upperDate}`;
    };

    const nextInstance =
      selectedInstanceIndex < instances.length - 1
        ? instances[selectedInstanceIndex + 1]
        : null;

    const previousInstance =
      selectedInstanceIndex > 0 ? instances[selectedInstanceIndex - 1] : null;

    const earliestInstance = instances[0];
    const latestInstance = instances[instances.length - 1];

    return {
      previousTooltip: previousInstance
        ? `Switch to GPS Lost for period ${getTimerangeDisplay(
            previousInstance.timerange,
          )}\nEarliest: ${getTimerangeDisplay(earliestInstance?.timerange)}`
        : 'No earlier GPS Lost period available',
      nextTooltip: nextInstance
        ? `Switch to GPS Lost for period ${getTimerangeDisplay(
            nextInstance.timerange,
          )}\nLatest: ${getTimerangeDisplay(latestInstance?.timerange)}`
        : 'No later GPS Lost period available',
    };
  }, [selectedInstanceIndex, instances, formatDateTimeWithTimeZone]);

  return (
    <PeriodNavigationActions
      onNext={onNext}
      onPrevious={onPrevious}
      isLoading={isAdding}
      isPreviousDisabled={isPreviousDisabled}
      isNextDisabled={isNextDisabled}
      previousTooltip={tooltips.previousTooltip}
      nextTooltip={tooltips.nextTooltip}
      shouldShow={instances.length > 0}
      setDisableDragNnDropTooltip={setDisableDragNnDropTooltip}
      isDragging={isDragging}
      variant={variant}
    />
  );
};
